import './BarcodeModal.scss';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Input, Dropdown } from 'semantic-ui-react';
import { ValueChangedForFieldFunc } from '../../formeditor-types';
import { BrowserMultiFormatReader } from '@zxing/library';
import GfModal from '../../../gfcomponents/GfModal/GfModal';

export interface BarcodeModalProps {
  name?: string;
  value?: string;
  valueUpdated: ValueChangedForFieldFunc;
  onClose: VoidFunc;
}

export default function BarcodeModal(props: BarcodeModalProps) {
  const { name, valueUpdated, onClose } = props;
  const [value, setValue] = useState(props.value);
  const [selectedVideoInputDeviceId, setSelectedVideoInputDeviceId] = useState<string>();
  const [videoInputDevices, setVideoInputDevices] = useState([]);
  const [scanning, setScanning] = useState(true);

  const videoEl = useRef<TypeMePlease>();

  const getCodeReaderInstance = async () => {
    const { BrowserMultiFormatReader } = await import(/* webpackChunkName: "zxing" */ '@zxing/library');
    return new BrowserMultiFormatReader();
  };

  const getCameraPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getCameraPermission()
      .then(() => getCodeReaderInstance())
      .then((codeReader) => codeReader.listVideoInputDevices())
      .then((devices) => {
        setVideoInputDevices(devices);
        if (devices.length > 0) {
          // auto select firrst device if available
          setSelectedVideoInputDeviceId(devices[0].deviceId);
        }
      });
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;
    let codeReader: BrowserMultiFormatReader;

    if (selectedVideoInputDeviceId && scanning) {
      getCodeReaderInstance()
        .then((cr) => {
          if (cancelled) {
            return;
          }

          codeReader = cr;
          return codeReader.decodeOnceFromVideoDevice(selectedVideoInputDeviceId, videoEl.current);
        })
        .then((result) => {
          if (cancelled) {
            return;
          }
          setValue(result.getText());
          setScanning(false);
          codeReader.reset();
        })
        .catch(() => {
          // we want to ignore these
        });
    }

    return () => {
      cancelled = true;
      if (codeReader) {
        codeReader.reset();
      }
    };
  }, [selectedVideoInputDeviceId, scanning]);

  const handleDoneButtonClicked = () => {
    valueUpdated({ value: value });
    onClose();
  };

  return (
    <GfModal
      title={name}
      className="barcode-modal"
      primaryText="Done"
      tertiaryText="Cancel"
      onPrimaryClick={handleDoneButtonClicked}
      onTertiaryClick={onClose}
      onClose={onClose}
    >
      <div className="barcode-modal-body">
        <div className="barcode-input-wrapper">
          <Input value={value || ''} onChange={(e) => setValue(e.target.value)} fluid placeholder="Scan or enter barcode" />
        </div>
        <div className="barcode-video-wrapper">
          {selectedVideoInputDeviceId ? <video ref={videoEl} /> : <div className="barcode-video-none">No camera selected</div>}
          {value && !scanning ? (
            <div className="scanning-complete">
              <div className="scanning-complete-text">Scanning Complete</div>
              <Button
                onClick={() => {
                  setScanning(true);
                  setValue('');
                }}
              >
                Rescan
              </Button>
            </div>
          ) : null}
        </div>
        <div className="barcode-device-selection">
          <span className="barcode-device-selection-label">Select Camera</span>
          <Dropdown
            selection
            value={selectedVideoInputDeviceId}
            options={videoInputDevices.map((x) => {
              return { text: x.label, value: x.deviceId };
            })}
            onChange={(e, v) => setSelectedVideoInputDeviceId(v.value as string)}
          />
        </div>
      </div>
    </GfModal>
  );
}
